// Entry requirements (MEMBERSHIPS_PLAN.md §6 "Competition settings: the Entry
// requirements step", D15/D16/D30/D33/D41, ME8): the pure half of
// EntryRequirementsFields. Who can enter + member prices live on the
// competition request as `whoCanEnter` ('Anyone' | 'AnyMembership' | 'Types')
// and `memberPrices` [{membershipTypeId, eligible, feeMinor}] — exactly the
// API's CompetitionMemberPriceRequest. A row's feeMinor: null = the standard
// price (no price row), 0 = free with that type, > 0 = that price. The copy
// builders mirror the server's CompetitionEntryRules so the live preview reads
// like the saved summary.

import {nameSaysNoun, nounFor, withNoun} from './membershipWords.js';
export const WHO_CAN_ENTER = Object.freeze({anyone: 'Anyone', anyMembership: 'AnyMembership', types: 'Types'});

export const PAYMENT_STATE = Object.freeze({
  hidden: 'hidden',
  unknown: 'unknown',
  notStarted: 'notStarted',
  onboarding: 'onboarding',
  checking: 'checking',
  enabled: 'enabled',
  restricted: 'restricted',
  disconnected: 'disconnected',
});

// ClubPaymentAccountStatus (SLL.Api ClubPaymentAccount.cs)
const ACCOUNT_STATUS = Object.freeze({created: 0, onboardingStarted: 1, detailsSubmitted: 2, chargesEnabled: 3, restricted: 4, disconnected: 5});

/**
 * One source for every payment-account state the entry step must handle
 * (none / onboarding / being checked / enabled / restricted / disconnected /
 * switched off / lookup failed). The meet form used to say "This meet is free"
 * for a Restricted account; it reads the same state as everything else now.
 */
export function paymentAccountState({account, lookupFailed = false, paymentsHidden = false} = {}) {
  if (paymentsHidden) return PAYMENT_STATE.hidden;
  if (lookupFailed) return PAYMENT_STATE.unknown;
  if (!account) return PAYMENT_STATE.notStarted;
  switch (account.status) {
    case ACCOUNT_STATUS.chargesEnabled: return PAYMENT_STATE.enabled;
    case ACCOUNT_STATUS.detailsSubmitted: return PAYMENT_STATE.checking;
    case ACCOUNT_STATUS.restricted: return PAYMENT_STATE.restricted;
    case ACCOUNT_STATUS.disconnected: return PAYMENT_STATE.disconnected;
    default: return PAYMENT_STATE.onboarding;
  }
}

/** Prices can be typed only when the account takes payments. */
export function canEditPrices(state) {
  return state === PAYMENT_STATE.enabled;
}

/**
 * The words for a state that is not "enabled", with the action the box offers.
 * Every title and body is a whole sentence: the button sits after them on its
 * own, never in the middle of a sentence (a copied or read-out box once said
 * "to charge for entry." on a line by itself). The membership note is said only
 * where a membership rule can be chosen (memberships switched on), and a box
 * for a competition that already has a price never calls it free.
 */
const article = (word) => (/^[aeiou]/i.test(String(word ?? '')) ? 'An' : 'A');

export function paymentStateCopy(state, {entityLabel = 'competition', meet = false, membershipsOn = true, priced = false} = {}) {
  const free = meet ? 'free to join' : 'free to enter';
  const membershipNote = membershipsOn
      ? `${article(entityLabel)} ${entityLabel} can need a membership or pass without payments. Free memberships and passes work too.`
      : null;
  switch (state) {
    case PAYMENT_STATE.unknown:
      return {tone: 'warn', title: "Couldn't check payments for this club.", body: null, action: 'retry'};
    case PAYMENT_STATE.notStarted:
      return priced
          ? {tone: 'warn', title: "Payments aren't set up for this club.", body: 'The price is kept, but nobody can pay it until payments are set up.', action: 'setUp', note: null}
          : {
            tone: 'info', title: `This ${entityLabel} is ${free}.`,
            body: meet ? 'Set up payments to charge attendees.' : 'Set up payments to charge for entry.',
            action: 'setUp', note: membershipNote,
          };
    case PAYMENT_STATE.onboarding:
      return priced
          ? {tone: 'warn', title: "Payments setup isn't finished.", body: 'The price is kept, but nobody can pay it until setup is finished.', action: 'continue'}
          : {tone: 'info', title: "Payments setup isn't finished.", body: `Until it is, this ${entityLabel} is ${free}.`, action: 'continue', note: membershipNote};
    case PAYMENT_STATE.checking:
      return {tone: 'warn', title: "Stripe is checking the club's details.", body: 'Prices can be set once it finishes. Anything already set is kept.', action: 'openPayments'};
    case PAYMENT_STATE.restricted:
      return {tone: 'warn', title: 'Payments paused: Stripe needs attention.', body: 'Prices are kept and still owed, but nobody can pay until the account is fixed.', action: 'openPayments'};
    case PAYMENT_STATE.disconnected:
      return {tone: 'warn', title: "The club's payment account is disconnected.", body: 'Prices are kept, but nobody can pay until it is reconnected.', action: 'openPayments'};
    default:
      return null;
  }
}

// ── The rows ────────────────────────────────────────────────────────────────

export function isRestricted(whoCanEnter) {
  return whoCanEnter === WHO_CAN_ENTER.anyMembership || whoCanEnter === WHO_CAN_ENTER.types;
}

export function rowFor(memberPrices, typeId) {
  return (memberPrices ?? []).find(r => r.membershipTypeId === typeId) ?? null;
}

export function isTicked(memberPrices, typeId) {
  return !!rowFor(memberPrices, typeId)?.eligible;
}

export function hasPriceRow(memberPrices, typeId) {
  const row = rowFor(memberPrices, typeId);
  return !!row && row.feeMinor != null;
}

/** The rows under "+ different price for a membership" (feeMinor set, 0 = free). */
export function priceRows(memberPrices) {
  return (memberPrices ?? []).filter(r => r.feeMinor != null);
}

export function tickedTypeIds(memberPrices) {
  return (memberPrices ?? []).filter(r => r.eligible).map(r => r.membershipTypeId);
}

/** Drops rows that neither tick nor price a type (what the API treats as "no row"). */
export function cleanMemberPrices(memberPrices) {
  return (memberPrices ?? []).filter(r => r.eligible || r.feeMinor != null)
      .map(r => ({membershipTypeId: r.membershipTypeId, eligible: !!r.eligible, feeMinor: r.feeMinor ?? null}));
}

export function setTicked(memberPrices, typeId, on) {
  const rows = (memberPrices ?? []).map(r => ({...r}));
  const row = rows.find(r => r.membershipTypeId === typeId);
  if (row) row.eligible = !!on;
  else if (on) rows.push({membershipTypeId: typeId, eligible: true, feeMinor: null});
  return cleanMemberPrices(rows);
}

/**
 * "+ different price for a membership": a new row starts empty (free). In
 * "Membership or pass needed" mode a priced type is ticked as well (ME8).
 */
export function addPriceRow(memberPrices, typeId, whoCanEnter) {
  const rows = (memberPrices ?? []).map(r => ({...r}));
  const row = rows.find(r => r.membershipTypeId === typeId);
  const tick = whoCanEnter === WHO_CAN_ENTER.types;
  if (row) {
    if (row.feeMinor == null) row.feeMinor = 0;
    if (tick) row.eligible = true;
  } else {
    rows.push({membershipTypeId: typeId, eligible: tick, feeMinor: 0});
  }
  return cleanMemberPrices(rows);
}

export function setPrice(memberPrices, typeId, feeMinor) {
  const rows = (memberPrices ?? []).map(r => ({...r}));
  const row = rows.find(r => r.membershipTypeId === typeId);
  if (row) row.feeMinor = feeMinor ?? 0;
  return cleanMemberPrices(rows);
}

/** Remove = back to the standard price; the tick (if any) stays. */
export function removePriceRow(memberPrices, typeId) {
  const rows = (memberPrices ?? []).map(r => ({...r}));
  const row = rows.find(r => r.membershipTypeId === typeId);
  if (row) row.feeMinor = null;
  return cleanMemberPrices(rows);
}

/** Whether the standard price box can apply to anyone (D33: hidden otherwise). */
export function standardPriceApplies(whoCanEnter, memberPrices) {
  if (whoCanEnter !== WHO_CAN_ENTER.types) return true;
  return (memberPrices ?? []).some(r => r.eligible && r.feeMinor == null);
}

/** ME1: paid = a standard price, or any row with a price above zero. */
export function isPaidRule(standardMinor, memberPrices) {
  if (standardMinor != null && standardMinor > 0) return true;
  return (memberPrices ?? []).some(r => (r.feeMinor ?? 0) > 0);
}

export function memberPricesEqual(a, b) {
  const key = (rows) => cleanMemberPrices(rows)
      .map(r => `${r.membershipTypeId}:${r.eligible ? 1 : 0}:${r.feeMinor ?? 'std'}`)
      .sort().join('|');
  return key(a) === key(b);
}

/** Whether the rule or any price changed between two competition shapes. */
export function entryRuleChanged(original, edited) {
  const who = (x) => x?.whoCanEnter ?? WHO_CAN_ENTER.anyone;
  return who(original) !== who(edited) || !memberPricesEqual(original?.memberPrices, edited?.memberPrices);
}

// ── Copy (mirrors CompetitionEntryRules / MembershipWording on the API) ─────

export function orList(names) {
  const list = names ?? [];
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} or ${list[1]}`;
  return `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}`;
}

export {nounFor};

/** "a Senior" / "an R1 Free Quick" / "an Under-18": the article by the sound of the first letter (the API's MembershipWording.Article). */
export function articleFor(name) {
  const trimmed = String(name ?? '').trimStart();
  if (!trimmed) return 'a';
  const first = trimmed[0].toLowerCase();
  const second = trimmed[1] ?? '';
  const initialism = trimmed.length > 1 && /[A-Z]/.test(trimmed[0]) && /[A-Z0-9]/.test(second);
  if (initialism) return 'aefhilmnorsx'.includes(first) ? 'an' : 'a';
  if (/^(uni|use|eu|one)/i.test(trimmed)) return 'a';
  if (/^(hour|honor|honour|heir)/i.test(trimmed)) return 'an';
  return 'aeiou'.includes(first) ? 'an' : 'a';
}

/** "with a Senior membership" / "with an R1 Free Quick membership" / "with a Tennis membership" / "with the Mixer pass" (X78: the noun once, withNoun). */
export function withPhrase(name, displayAs) {
  return displayAs === 'Pass' ? `with the ${name}` : `with ${articleFor(name)} ${withNoun(name, 'membership')}`;
}

/** "Senior or Junior membership" / "Mixer pass" / "Senior membership or Mixer pass" / "membership or pass". */
export function typesPhrase(whoCanEnter, types) {
  const list = (types ?? []).filter(Boolean);
  if (whoCanEnter !== WHO_CAN_ENTER.types || list.length === 0) return 'membership or pass';
  const short = (t) => String(t.name ?? '').trim();
  const endsWithNoun = (t) => nameSaysNoun(short(t), nounFor(t.displayAs));
  const joined = orList(list.map(short));
  if (list.every(t => t.displayAs !== 'Pass') && !list.some(endsWithNoun)) return `${joined} membership`;
  if (list.every(t => t.displayAs === 'Pass') && !list.some(endsWithNoun)) return `${joined} pass`;
  return orList(list.map(t => withNoun(short(t), t.displayAs)));
}

export function neededLine(whoCanEnter, eligibleTypes) {
  if (whoCanEnter === WHO_CAN_ENTER.anyMembership) return 'Any membership or pass needed';
  if (whoCanEnter === WHO_CAN_ENTER.types) {
    return (eligibleTypes ?? []).length ? `${typesPhrase(whoCanEnter, eligibleTypes)} needed` : 'Membership or pass needed';
  }
  return 'Anyone can enter';
}

/** "to enter" / "a team" / "a session". */
export function priceUnit(sourceType, playersPerTeam) {
  if (sourceType === 'Meet') return 'a session';
  if ((playersPerTeam ?? 1) > 1 && ['Ladder', 'League', 'LeagueCycle', 'Tournament'].includes(sourceType)) return 'a team';
  return 'to enter';
}

/**
 * "£10 to enter · £5 with a Senior membership · free with a Junior membership".
 * rows: [{name, displayAs, eligible, feeMinor, sortOrder?}] in display order.
 */
export function priceLine({whoCanEnter, standardMinor, currency, rows, unit, formatMoney}) {
  const money = (minor) => formatMoney(minor, currency);
  const withRow = (row, price) => `${price == null || price === 0 ? 'free' : money(price)} ${withPhrase(row.name, row.displayAs)}`;
  const std = standardMinor ?? null;
  const parts = [];
  const list = rows ?? [];
  if (whoCanEnter === WHO_CAN_ENTER.types && list.some(r => r.eligible)) {
    const eligible = list.filter(r => r.eligible);
    const payingStandard = eligible.filter(t => t.feeMinor == null);
    const ownPrice = eligible.filter(t => t.feeMinor != null);
    if (payingStandard.length && ((std != null && std > 0) || ownPrice.length === 0)) parts.push(std != null && std > 0 ? `${money(std)} ${unit}` : 'Free');
    else if (payingStandard.length) parts.push(...payingStandard.map(t => withRow(t, null)));
    parts.push(...ownPrice.map(t => withRow(t, t.feeMinor)));
    return parts.join(' · ');
  }
  const cheaper = list.filter(r => r.feeMinor != null && std != null && r.feeMinor < std);
  parts.push(std != null && std > 0 ? `${money(std)} ${unit}` : 'Free');
  parts.push(...cheaper.map(r => withRow(r, r.feeMinor)));
  return parts.join(' · ');
}

/** The "What players see" line: who can enter, then the prices (D33). */
export function entrySummary({whoCanEnter, standardMinor, currency, rows, unit, formatMoney}) {
  const prices = priceLine({whoCanEnter, standardMinor, currency, rows, unit, formatMoney});
  if (!isRestricted(whoCanEnter)) return prices;
  const eligible = (rows ?? []).filter(r => r.eligible);
  return `${neededLine(whoCanEnter, eligible)} · ${prices}`;
}

/** "Price per player" / "Price per team, per cycle" / "Price each session", or the D33 label when some ticked type pays it. */
export function standardPriceLabel({sourceType, payerLabel = 'player', cadence = null, whoCanEnter, memberPrices}) {
  if (whoCanEnter === WHO_CAN_ENTER.types && priceRows(memberPrices).length) return 'Price for anyone not listed below';
  if (sourceType === 'Meet') return 'Price each session';
  return cadence ? `Price per ${payerLabel}, per ${cadence}` : `Price per ${payerLabel}`;
}

/**
 * The sentence under the price rows: who pays the standard price, lowest price
 * wins, and (for teams) the mixed-pair rule (D16).
 */
export function priceNote({whoCanEnter, standardMinor, currency, rows, allTypes, playersPerTeam = 1, formatMoney, sourceType}) {
  const parts = [];
  const money = (minor) => formatMoney(minor, currency);
  const priced = (rows ?? []).filter(r => r.feeMinor != null);
  const std = standardMinor ?? 0;
  const stdText = std > 0 ? money(std) : 'nothing';
  const people = sourceType === 'Meet' ? 'People' : 'Players';
  if (priced.length) {
    const unlisted = (allTypes ?? []).filter(t => !priced.some(r => r.membershipTypeId === t.id));
    const whoElse = whoCanEnter === WHO_CAN_ENTER.types
        ? unlisted.filter(t => (rows ?? []).some(r => r.membershipTypeId === t.id && r.eligible))
        : unlisted;
    const names = whoElse.map(t => t.name);
    if (whoCanEnter === WHO_CAN_ENTER.types) {
      parts.push(names.length ? `${orList(names)} holders pay ${stdText}.` : 'Everyone who can enter has a price above.');
    } else {
      parts.push(names.length
          ? `Anyone without a price above pays ${stdText}, including ${orList(names)} holders.`
          : `Anyone without a price above pays ${stdText}.`);
    }
    parts.push(`${people} pay the lowest price any of their current memberships or passes gives them.`);
  }
  if ((playersPerTeam ?? 1) > 1 && priced.length) {
    parts.push('A mixed pair pays the higher price: the team price only drops when every player has the cheaper membership.');
  }
  return parts.join(' ');
}

/** The caption under the type ticks in "Membership or pass needed" mode. */
export function whoCanEnterCaption({playersPerTeam = 1, entityLabel = 'competition', sourceType}) {
  const team = (playersPerTeam ?? 1) > 1;
  if (sourceType === 'Meet') {
    return 'Checked on each date they book. Admins can still add anyone, with a warning.';
  }
  const first = team
      ? 'Every player on a team needs one of these. Tick any that count.'
      : 'Players need one of these. Tick any that count.';
  const tail = team
      ? `Both players in a pair need a ticked membership to join. Admins can still add anyone, with a warning. If a membership runs out during the ${entityLabel}, the player stays in and you'll see a flag.`
      : `Admins can still add anyone, with a warning. If a membership runs out during the ${entityLabel}, the player stays in and you'll see a flag.`;
  return `${first} ${tail}`;
}

/** D30: nobody holds a type that was just created here. */
export function nobodyHoldsLine(type, entityLabel = 'competition') {
  const noun = nounFor(type?.displayAs);
  return `Nobody has ${articleFor(type?.name)} ${withNoun(type?.name, noun)} yet, so nobody can join this ${entityLabel} until they do. Share the club page or add memberships yourself.`;
}

/** "Senior members pay £10 too. Add a different price for a membership to change that." (NoneAfterCreate) */
export function sameStandardLine(type, standardMinor, currency, formatMoney) {
  if (!type || !(standardMinor > 0)) return null;
  const holders = type.displayAs === 'Pass' ? `${type.name} holders` : `${type.name} members`;
  return `${holders} pay ${formatMoney(standardMinor, currency)} too. Add a different price for a membership to change that.`;
}

/** The viewer's own line on a competition page: "£5 for you" / "Free for you". */
export function viewerPriceLine(viewerFeeMinor, currency, formatMoney) {
  if (viewerFeeMinor == null) return null;
  return viewerFeeMinor === 0 ? 'Free for you' : `${formatMoney(viewerFeeMinor, currency)} for you`;
}

/** Rows for the copy builders from the editor state + the club's types. */
export function describeRows(memberPrices, types) {
  const byId = new Map((types ?? []).map(t => [t.id, t]));
  return cleanMemberPrices(memberPrices)
      .map(r => {
        const t = byId.get(r.membershipTypeId);
        return t ? {membershipTypeId: r.membershipTypeId, name: t.name, displayAs: t.displayAs, eligible: r.eligible, feeMinor: r.feeMinor, sortOrder: t.sortOrder ?? 0} : null;
      })
      .filter(Boolean)
      .sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name));
}

/** The type list the step shows: Live and Closed-to-new, never Draft or archived; the server's availableTypes when given. */
export function usableTypes(types) {
  return (types ?? []).filter(t => t && !t.archivedAt && t.status !== 'Draft' && t.status !== 0);
}

/** "£240 a year · 96 members" under a type tick. */
export function typeTickCaption(type, holders) {
  const parts = [];
  if (type?.priceSummary) parts.push(type.priceSummary);
  else if (type?.isFree) parts.push('Free');
  const n = holders ?? type?.currentCount ?? type?.holders ?? null;
  if (n != null) parts.push(`${n} ${n === 1 ? 'member' : 'members'}`);
  return parts.join(' · ');
}

/** The admin warning after a Place / approve / add: "Alex has no Senior membership" lines, joined. */
export function membershipWarningsText(warnings) {
  const list = (warnings ?? []).filter(Boolean);
  if (!list.length) return null;
  return `${list.join('. ')}. Added anyway.`;
}

/** The confirm dialog's last line: "The rule applies to new joins." / "…, and 2 waiting to join will be checked again." (no stray space; the API's own "They stay in" sentence is not repeated) */
export function entryRuleAppliesLine(pendingWithoutMembership) {
  return (pendingWithoutMembership ?? 0) > 0
      ? `The rule applies to new joins, and ${pendingWithoutMembership} waiting to join will be checked again.`
      : 'The rule applies to new joins.';
}
